// Product image resolution — single source of truth for every page.
//
// Priority:
//   1. product.images — photos uploaded from the farm-dashboard catalogue
//      page (stored on the farm server, first entry is the lead photo).
//   2. The legacy `image` folder field + imageManifest.json, which is
//      generated at build time by scripts/generate-image-manifest.js from
//      whatever is actually in public/images/products/.

import manifest from "../imageManifest.json";

// Public base for farm-server URLs (product.images entries look like
// /api/uploads/images/xyz.jpg and are served through the /garageFarm proxy).
const API_PUBLIC_BASE = "https://teardrophydro.co.uk/garageFarm";

// Preferred lead photo per legacy folder. Optional — folders not listed
// here lead with their first file alphabetically.
const LEAD_IN_FOLDER = {
  genoBasil: "IMG_5987.JPEG",
  chives: "IMG_4857.JPEG",
  cress: "IMG_4581.JPEG",
  dill: "IMG_5227.JPEG",
  komatsuna: "IMG_4483.JPEG",
  lettuce: "IMG_4447.JPEG",
  mix: "IMG_4391.JPEG",
  seedlings: "IMG_4469.JPEG",
  sorrel: "IMG_5044.JPEG",
  sunflowers: "sun2.png",
  mint: "IMG_6827.JPEG",
  salvoBasil: "IMG_4565.JPEG",
  coriander: "IMG_6755.JPEG",
  mizuna: "IMG_6786.JPEG",
  frenchMix: "IMG_6777.JPEG",
  mesclunMix: "IMG_6779.JPEG",
  leafBeet: "IMG_6901.JPEG",
  parsley: "IMG_6914.JPEG",
  redAmaranth: "IMG_7317.JPEG",
};

const hasExt = (s = "") => /\.[a-z0-9]{2,5}$/i.test(s);

// Map a stored URL to something the browser can load from the public site.
export function toPublicUrl(u) {
  const s = String(u || "");
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/api/")) return API_PUBLIC_BASE + s.slice(4);
  return s; // site-relative (/images/...) — served by the website itself
}

// Full ordered gallery for a product. Uploaded photos win outright;
// otherwise the legacy folder's manifest listing (lead file first).
export function galleryFor(product) {
  const uploaded = (product?.images || []).map(toPublicUrl).filter(Boolean);
  if (uploaded.length) return uploaded;

  const image = String(product?.image || "");
  if (!image) return [];
  if (hasExt(image)) return [image]; // direct file path — use as-is

  const base = image.replace(/\/+$/, "");
  const folder = base.split("/").pop();
  const files = manifest[folder] || [];
  const lead = LEAD_IN_FOLDER[folder];
  const ordered =
    lead && files.includes(lead)
      ? [lead, ...files.filter((f) => f !== lead)]
      : files;
  return ordered.map((f) => `${base}/${f}`);
}

// The single card/thumbnail image for a product, or null (caller shows a
// placeholder).
export function leadImageFor(product) {
  return galleryFor(product)[0] || null;
}

// Absolute URL for social/schema markup.
export function absoluteImageFor(product) {
  const lead = leadImageFor(product);
  if (!lead) return null;
  return /^https?:\/\//i.test(lead)
    ? lead
    : `https://teardrophydro.co.uk${lead.startsWith("/") ? "" : "/"}${lead}`;
}
